// The DAILY PLAN of the ALERTS section of the hub SETTINGS panel
// (player-reach.md §3.3 "The daily plan", R3280).
//
// What it shows: the hour of the daily plan push as one choice of OFF and
// the 24 hours of the day, and the time zone of the browser, which the
// card sends with the hour. Once a day at that local hour, the meta server
// names the account to each world that seats it, and the world sends one
// push notification with the siege windows of the next world day that the
// faction defends or attacks.
//
// The hour is an account setting, but NOT a key of the settings blob: the
// heartbeat reads it, so it travels on its own route
// (`PUT /api/settings/daily-plan`), like the stated hours (`hours.ts`). A
// press writes at once and is never debounced. OFF is the default: the
// push is an opt-in, as every push class is (§3.1).

import { getSettings, putDailyPlan, type StoredSettings } from '../../../net/api';
import { hubDailyPlan as S } from '../../../gen/strings';
import { TTL_ON_OPEN, zoneLabel, type Cell } from '../hubstate';
import { valueChoice } from '../../choice/choice';
import { groupHead, note, row } from '../../options/kit';
import type { PanelContext } from '../panel';
import { SETTINGS_TYPED_CELL } from './hours';
import { errNote, runHeld, type HeldCall } from './optcards';

/** The id of OFF in the choice; an hour's id is its number. */
const OFF = 'off';

/** The part of a panel the SETTINGS panel mounts. */
export interface DailyPlanCard {
  html(): string;
  /** Takes one press. Answers false for an action that is not the card's. */
  act(action: string, el: HTMLElement): boolean;
}

/** The IANA time zone of this browser, or UTC when it names none. */
function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/** One hour as hh:00. */
export function hourLabel(h: number): string {
  return `${String(h).padStart(2, '0')}:00`;
}

/** The choice id of an hour, or of OFF for null. */
function idOf(hour: number | null): string {
  return hour === null ? OFF : String(hour);
}

/** The hour of a choice id: null for OFF, undefined for an id that is no
 *  hour. */
export function hourOf(id: string): number | null | undefined {
  if (id === OFF) return null;
  const h = Number(id);
  return Number.isInteger(h) && h >= 0 && h <= 23 && id !== '' ? h : undefined;
}

/** `heading` writes the group head as an `h3`, the level of the hub
 *  outline (R2048). */
export function createDailyPlanCard(
  ctx: PanelContext, zone: () => string = browserTimeZone, heading = false,
): DailyPlanCard {
  /** The hour as the last press left it, or undefined while the card
   *  shows what the server answered. */
  let picked: number | null | undefined;
  const st: HeldCall = { busy: false, err: '' };

  /** The hour in force, or undefined while the first read travels. A
   *  server that predates the field reads as OFF. */
  function hourNow(): number | null | undefined {
    if (picked !== undefined) return picked;
    const read: Cell<StoredSettings> = ctx.data.cell<StoredSettings>(SETTINGS_TYPED_CELL, TTL_ON_OPEN, getSettings);
    if (read.state !== 'ready') return undefined;
    return read.value?.dailyPlan?.hour ?? null;
  }

  /** One press on a choice. It writes at once, and the card shows the
   *  hour the server answered. */
  function pick(hour: number | null): void {
    const now = hourNow();
    if (st.busy || now === undefined || hour === now) return;
    runHeld(ctx, st, 'sfx-menu-toggle', () => putDailyPlan(hour, zone()), (r) => {
      picked = r.dailyPlan.hour;
      ctx.data.drop(SETTINGS_TYPED_CELL);
    });
  }

  return {
    html(): string {
      const now = hourNow();
      const choices = [{ id: OFF, label: S.off }]
        .concat(Array.from({ length: 24 }, (_, h) => ({ id: String(h), label: hourLabel(h) })));
      const desc = now === undefined ? ''
        : ` · ${now === null ? S.off_desc : S.at_desc({ hour: hourLabel(now) })}`;
      return groupHead(S.head, now === undefined ? S.checking : '', '', heading)
        + note(S.lede)
        + row({ name: S.hour, what: S.hour_desc({ zone: zoneLabel(zone(), ctx.now()) }) + desc, control: '' })
        + valueChoice({ list: choices, chosen: now === undefined ? '' : idOf(now), act: 'dailyplan', label: S.head, uid: 'hub-dailyplan' })
        + errNote(st.err);
    },

    act(action: string, el: HTMLElement): boolean {
      if (action !== 'dailyplan') return false;
      const hour = hourOf(el.dataset.id ?? '');
      if (hour !== undefined) pick(hour);
      return true;
    },
  };
}
